/**
 * Aylık iaşe raporu (ADR 0123): Inventory ▸ Victualling Report. İngilizce varsayılan, Türkçe
 * ikinci dil. Para birimi başına tutarlar; birimler toplanmaz.
 */
export const victuallingEn = {
  title: 'Victualling Report',
  head: 'Victualling report',
  sections: 'Inventory sections',
  vessel: 'Vessel',
  fleet: 'All vessels',
  month: 'Month',
  period: 'Period',
  status: 'Status',
  complete: 'Month closed',
  toDate: 'To date',
  exportPdf: 'PDF',
  exportCsv: 'CSV (Excel)',
  budget: 'Budget',
  summary: 'Victualling summary',
  retry: 'Try again',
  closeNotice: 'Close message',
  cards: {
    personDays: 'Person-days',
    averagePob: 'Average POB',
    consumption: 'Crew consumption',
    rate: 'Victualling rate / day',
    budget: 'Budget variance',
    vessels: 'Vessels',
    overBudget: 'Over budget',
    unpriced: 'Unpriced items',
    fleetRate: 'Fleet rate / day',
  },
  rates: {
    title: 'Victualling Rate by Currency',
    table: 'Victualling rate by currency table',
    currency: 'Currency',
    consumption: 'Crew consumption',
    rate: 'Rate / person-day',
    budgetRate: 'Budget / day',
    variance: 'Variance',
    noBudget: 'No budget',
    none: 'No crew provision consumption with a price in this month.',
    noPersonDays: 'No crew assignments in this month; the rate needs person-days.',
    basis:
      'Crew provisions only (cost center “Crew Provisions” or not set). Currencies are never added together.',
  },
  categories: {
    title: 'Stock Movement by Category',
    table: 'Stock movement by category table',
    currency: 'Currency',
    category: 'Category',
    items: 'Items',
    opening: 'Opening',
    receipts: 'Receipts',
    consumption: 'Consumption',
    disposal: 'Write-off',
    adjustments: 'Count diff.',
    closing: 'Closing',
    total: 'Total',
    unpriced: '{{count}} unpriced',
    empty: 'No provision movement in this month.',
    equation: 'Opening + receipts − consumption − write-off ± count difference = closing.',
    noCode: 'Other',
  },
  items: {
    title: 'Items',
    table: 'Items table',
    item: 'Item',
    unit: 'Unit',
    value: 'Consumption ({{currency}})',
    valueAny: 'Consumption value',
    notInRate: 'Not in rate',
    unpriced: 'Unpriced',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    pagination: 'Item pages',
    page: {
      first: 'First page',
      previous: 'Previous page',
      next: 'Next page',
      last: 'Last page',
      number: 'Page {{page}}',
    },
    empty: 'No provision items with movement in this month.',
  },
  costCenters: {
    title: 'Consumption by Cost Center',
    table: 'Consumption by cost center table',
    costCenter: 'Cost center',
    consumption: 'Consumption',
    disposal: 'Write-off',
    inRate: 'In rate',
    none: 'Not set',
    yes: 'Yes',
    no: 'No',
    empty: 'No consumption in this month.',
  },
  pob: {
    title: 'Crew on Board',
    average: 'Average',
    peak: 'Peak',
    days: 'Days',
    chart: 'Daily persons on board',
    note: 'From crew assignments (sign-on and sign-off dates).',
  },
  fleetView: {
    title: 'Fleet Victualling',
    table: 'Fleet victualling table',
    vessel: 'Vessel',
    open: 'Open the report of {{name}}',
    total_one: 'Fleet ({{count}} vessel)',
    total_other: 'Fleet ({{count}} vessels)',
    empty: 'No vessel to show.',
    noConsumption: 'No priced crew consumption',
  },
  budgetDialog: {
    title: 'Victualling budget',
    hint: 'Daily budget per person for each currency. Reports compare only in the same currency.',
    currency: 'Currency',
    dailyRate: 'Daily rate per person',
    add: 'Add currency',
    remove: 'Remove {{currency}}',
    save: 'Save budget',
    cancel: 'Cancel',
    saved: 'The budget is saved; vessels receive it with the next synchronisation.',
    duplicate: 'Each currency can be entered once.',
    invalid: 'Enter a three-letter currency code and a rate above zero.',
    empty: 'No budget yet. Add a currency.',
  },
  pdf: {
    title: 'Victualling Report — {{month}}',
    fleetTitle: 'Fleet Victualling — {{month}}',
    printed: 'Printed {{at}}',
    period: 'Period {{from}} – {{to}}',
    partial: 'Month to date',
    master: 'Master',
    chiefOfficer: 'Chief Officer',
    office: 'Office review',
  },
  notices: { file: '{{name}} saved.' },
  errors: {
    load: 'The victualling report could not be loaded.',
    FUTURE_DATE: 'This month has not started on board yet.',
    INVALID_MONTH: 'Choose a valid month.',
    FORBIDDEN: 'You cannot see provisions of this vessel.',
    VALIDATION_FAILED: 'Choose a valid month.',
  },
};

export const victuallingTr: typeof victuallingEn = {
  title: 'İaşe Raporu',
  head: 'İaşe raporu',
  sections: 'Envanter bölümleri',
  vessel: 'Gemi',
  fleet: 'Bütün gemiler',
  month: 'Ay',
  period: 'Dönem',
  status: 'Durum',
  complete: 'Ay kapandı',
  toDate: 'Bugüne kadar',
  exportPdf: 'PDF',
  exportCsv: 'CSV (Excel)',
  budget: 'Bütçe',
  summary: 'İaşe özeti',
  retry: 'Yeniden dene',
  closeNotice: 'Mesajı kapat',
  cards: {
    personDays: 'Kişi-gün',
    averagePob: 'Ortalama POB',
    consumption: 'Mürettebat tüketimi',
    rate: 'İaşe oranı / gün',
    budget: 'Bütçe farkı',
    vessels: 'Gemiler',
    overBudget: 'Bütçe üstü',
    unpriced: 'Fiyatsız kalem',
    fleetRate: 'Filo oranı / gün',
  },
  rates: {
    title: 'Para Birimine Göre İaşe Oranı',
    table: 'Para birimine göre iaşe oranı tablosu',
    currency: 'Para birimi',
    consumption: 'Mürettebat tüketimi',
    rate: 'Oran / kişi-gün',
    budgetRate: 'Bütçe / gün',
    variance: 'Fark',
    noBudget: 'Bütçe yok',
    none: 'Bu ay fiyatı bilinen mürettebat kumanyası tüketimi yok.',
    noPersonDays: 'Bu ay gemide atama yok; oran kişi-gün ister.',
    basis:
      'Yalnız mürettebat kumanyası (maliyet merkezi “Crew Provisions” ya da boş). Para birimleri toplanmaz.',
  },
  categories: {
    title: 'Kategoriye Göre Stok Hareketi',
    table: 'Kategoriye göre stok hareketi tablosu',
    currency: 'Para birimi',
    category: 'Kategori',
    items: 'Kalem',
    opening: 'Açılış',
    receipts: 'Giriş',
    consumption: 'Tüketim',
    disposal: 'Zayi',
    adjustments: 'Sayım farkı',
    closing: 'Kapanış',
    total: 'Toplam',
    unpriced: '{{count}} fiyatsız',
    empty: 'Bu ay kumanya hareketi yok.',
    equation: 'Açılış + giriş − tüketim − zayi ± sayım farkı = kapanış.',
    noCode: 'Diğer',
  },
  items: {
    title: 'Kalemler',
    table: 'Kalem tablosu',
    item: 'Kalem',
    unit: 'Birim',
    value: 'Tüketim ({{currency}})',
    valueAny: 'Tüketim değeri',
    notInRate: 'Orana girmez',
    unpriced: 'Fiyatsız',
    showing: '{{total}} kalemden {{from}}–{{to}}',
    pagination: 'Kalem sayfaları',
    page: {
      first: 'İlk sayfa',
      previous: 'Önceki sayfa',
      next: 'Sonraki sayfa',
      last: 'Son sayfa',
      number: 'Sayfa {{page}}',
    },
    empty: 'Bu ay hareketi olan kumanya kalemi yok.',
  },
  costCenters: {
    title: 'Maliyet Merkezine Göre Tüketim',
    table: 'Maliyet merkezine göre tüketim tablosu',
    costCenter: 'Maliyet merkezi',
    consumption: 'Tüketim',
    disposal: 'Zayi',
    inRate: 'Orana girer',
    none: 'Belirtilmemiş',
    yes: 'Evet',
    no: 'Hayır',
    empty: 'Bu ay tüketim yok.',
  },
  pob: {
    title: 'Gemideki Mürettebat',
    average: 'Ortalama',
    peak: 'En çok',
    days: 'Gün',
    chart: 'Günlük gemideki kişi sayısı',
    note: 'Mürettebat atamalarından (katılış ve ayrılış tarihleri).',
  },
  fleetView: {
    title: 'Filo İaşesi',
    table: 'Filo iaşe tablosu',
    vessel: 'Gemi',
    open: '{{name}} raporunu aç',
    total_one: 'Filo ({{count}} gemi)',
    total_other: 'Filo ({{count}} gemi)',
    empty: 'Gösterilecek gemi yok.',
    noConsumption: 'Fiyatlı mürettebat tüketimi yok',
  },
  budgetDialog: {
    title: 'İaşe bütçesi',
    hint: 'Her para birimi için kişi başı günlük bütçe. Raporlar yalnız aynı birimde karşılaştırır.',
    currency: 'Para birimi',
    dailyRate: 'Kişi başı günlük oran',
    add: 'Para birimi ekle',
    remove: '{{currency}} kaldır',
    save: 'Bütçeyi kaydet',
    cancel: 'Vazgeç',
    saved: 'Bütçe kaydedildi; gemiler bir sonraki senkronda alır.',
    duplicate: 'Her para birimi bir kez girilir.',
    invalid: 'Üç harfli para birimi kodu ve sıfırdan büyük oran girin.',
    empty: 'Henüz bütçe yok. Para birimi ekleyin.',
  },
  pdf: {
    title: 'İaşe Raporu — {{month}}',
    fleetTitle: 'Filo İaşesi — {{month}}',
    printed: 'Basım {{at}}',
    period: 'Dönem {{from}} – {{to}}',
    partial: 'Ay içi (bugüne kadar)',
    master: 'Kaptan',
    chiefOfficer: 'Birinci Zabit',
    office: 'Ofis incelemesi',
  },
  notices: { file: '{{name}} kaydedildi.' },
  errors: {
    load: 'İaşe raporu yüklenemedi.',
    FUTURE_DATE: 'Bu ay gemide henüz başlamadı.',
    INVALID_MONTH: 'Geçerli bir ay seçin.',
    FORBIDDEN: 'Bu geminin kumanyasını göremezsiniz.',
    VALIDATION_FAILED: 'Geçerli bir ay seçin.',
  },
};
